import FontAwesome from 'react-native-vector-icons/FontAwesome';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useEffect, useMemo, useState } from 'react';
import {
    ActivityIndicator,
    Image,
    Platform,
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    useWindowDimensions,
    View
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTheme } from '../context/ThemeContext';
import { useT } from '../i18n/useT';
import { useAuth } from '../context/AuthContext';
import { getThemeColors } from '../utils/themeStyles';
import { useMissions, ActiveMissionData, MarketplaceMission, TrialMission, CompanyMissionData } from '../context/MissionsContext';
import { usePreferredLanguage } from '../hooks/usePreferredLanguage';
import { useMissionLaunch } from '../hooks/useMissionLaunch';
import { missionLanguages, missionMatchesLanguage } from '../utils/preferredLanguage';
import RemoteImage from '../components/RemoteImage';
import { azuriteFallback } from '../config/env';

/** Avatars de L'ÉQUIPE de la mission, pour remplacer une vignette absente (CCT-256).
 *
 *  Lisait `MISSION_STORY_KEY_MAP` — une table id→histoire codée en dur, qui ne connaît qu'une
 *  poignée de missions : toutes les autres tombaient à zéro et la carte affichait un cadre vide
 *  avec une icône d'image. C'est le même défaut que sur la page de détails (CCT-254).
 *  On lit désormais les personnages servis avec la mission, joueur en tête.
 */
function getMissionAvatars(mission: { characters?: any[] }): any[] {
  const chars = (mission?.characters ?? []).filter((c: any) => c?.avatarUrl);
  return [...chars]
    .sort((a, b) => Number(!!b.isPlayer) - Number(!!a.isPlayer))
    .slice(0, 4)
    .map((c: any) => ({ uri: c.avatarUrl }));
}

interface FilterState {
  domain: string;
  provider: string; // pour Cloud : Azure / AWS / GCP… (choisi avant la certif)
  certification: string;
}

const CERT_GROUPS: Record<string, Array<{ label: string; items: string[] }>> = {
  Cloud: [
    { label: 'Azure', items: ['AZ-900', 'AZ-104', 'AZ-305', 'AZ-400', 'AZ-500', 'AZ-700', 'AZ-220'] },
    { label: 'AWS', items: ['AWS Cloud Practitioner', 'AWS Developer', 'AWS DevOps', 'AWS AI Practitioner'] },
    { label: 'GCP', items: ['GCP Cloud Engineer', 'GCP Cloud Architect'] },
    { label: 'Data', items: ['DP-900', 'DP-203', 'DP-300'] },
    { label: 'Security', items: ['SC-900'] },
    { label: 'Power Platform', items: ['PL-900'] },
    { label: 'AI', items: ['AI-900'] },
  ],
  Science: [
    { label: 'Topics', items: ['Python', 'Data Science', 'Machine Learning'] },
  ],
  Languages: [
    { label: 'Languages', items: ['French', 'English', 'Spanish', 'Arabic'] },
  ],
};

const CLOUD_CERT_SET = new Set(CERT_GROUPS.Cloud.flatMap(group => group.items.map(item => item.trim().toUpperCase())));
const SCIENCE_CERT_SET = new Set(CERT_GROUPS.Science.flatMap(group => group.items.map(item => item.trim().toUpperCase())));
const LANGUAGE_CERT_SET = new Set(CERT_GROUPS.Languages.flatMap(group => group.items.map(item => item.trim().toUpperCase())));

export default function MissionsScreen() {
  const navigation = useNavigation<any>();
  const { isDarkTheme } = useTheme();
  const t = useT();
  const { user } = useAuth();
  const colors = getThemeColors(isDarkTheme);
  const { activeMissions, trialMissions, companyMissions, startTrial, cancelTrial, addToCart, isInTrial, isInCart, isOwned, allMarketplaceMissions, marketplaceLoading, cartItems } = useMissions();
  // Badges dont l'image ne charge pas (asset manquant sur le blob) -> on les
  // masque au lieu d'afficher une image cassée.
  const [failedBadges, setFailedBadges] = useState<Set<number>>(new Set());
  const markBadgeFailed = (id: number) =>
    setFailedBadges(prev => (prev.has(id) ? prev : new Set(prev).add(id)));
  // Covers distantes manquantes (404 sur le blob) -> repli sur le montage d'avatars locaux.
  // Cover : image distante (blob) en priorité ; à défaut, banner LOCAL résolu par le
  // nom de la mission ; en dernier recours, montage d'avatars. Géré par <RemoteImage>.
  // Vignette : la couverture d'abord, puis la BANNIÈRE AUTHORÉE servie par le runtime
  // (`bannerWeb`). Auparavant le second recours était une image BUNDLÉE, choisie par une table
  // id→histoire codée en dur — donc fausse dès qu'on quitte le local, où les identifiants de
  // mission diffèrent. Sans image, la rangée d'avatars de l'équipe prend le relais.
  const coverUri = (m: { coverImage?: any; bannerWeb?: any }): string | undefined => {
    const src = m.coverImage ?? m.bannerWeb;
    return typeof src === 'string' ? src : src?.uri;
  };
  const avatarBand = (mission: { characters?: any[] }) => {
    const avs = getMissionAvatars(mission);
    if (avs.length === 0) return null;
    return (
      <View style={[styles.missionCoverThumb, styles.avatarBand, { backgroundColor: '#0F172A' }]}>
        <View style={styles.avatarBandRow}>
          {avs.map((av, idx) => (
            <View key={idx} style={[styles.avatarBandCircle, idx > 0 && styles.avatarBandOverlap]}>
              <Image source={av} style={styles.avatarBandImg} resizeMode="cover" />
            </View>
          ))}
        </View>
      </View>
    );
  };

  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const isWebDesktop = Platform.OS === 'web' && isDesktop;

  const [activeTab, setActiveTab] = useState<'active' | 'trial' | 'company' | 'marketplace'>(user ? 'active' : 'marketplace');

  // Essai gratuit : l'action EXIGE un compte (elle crée un Assignment côté serveur). En
  // déconnecté, `startTrial` retournait en SILENCE et le bouton semblait mort (CCT-253) : on
  // route désormais vers la connexion, en mémorisant la mission pour y revenir après login.
  const beginTrial = async (missionId: number) => {
    const issue = await startTrial(missionId);
    if (issue === 'needsLogin') {
      try {
        sessionStorage.setItem('ccjaw.returnTo', `/mission/${missionId}`);
      } catch {
        /* pas de sessionStorage (natif) : on ouvre simplement la connexion */
      }
      navigation.navigate('Login' as never);
      return;
    }
    if (issue === 'started') {
      // ⚠️ L'ESSAI OUVRE LE DROIT, IL NE DÉMARRE PAS LA PARTIE (CCT-286). C'est `launch` qui
      // demande la langue, la fige et allume le moteur — sans quoi l'essai serait jouable avec
      // un moteur éteint, donc sans jamais matérialiser la moindre user story.
      await launch(missionId, () => setActiveTab('trial'));
    }
  };

  // Filtre par entreprise (depuis le menu latéral : clic sur une entreprise sous « Missions »).
  const route = useRoute<any>();
  const companyFilter: number | undefined = route.params?.companyId;
  const companyFilterName: string | undefined = route.params?.companyName;
  // Missions d'entreprise affichées : filtrées sur l'entreprise sélectionnée le cas échéant.
  const shownCompanyMissions = companyFilter != null
    ? companyMissions.filter(m => m.companyId === companyFilter)
    : companyMissions;
  // Clic sur une entreprise dans le menu -> bascule sur l'onglet Company.
  useEffect(() => {
    if (companyFilter != null && user) setActiveTab('company');
  }, [companyFilter, user]);
  // L'onglet Company n'existe que s'il y a des missions company : si on y est et qu'il
  // n'y en a plus (ou plus connecté), on bascule sur un onglet visible.
  useEffect(() => {
    // On reste sur l'onglet Company si un filtre entreprise est active (affiche l'état vide).
    if (activeTab === 'company' && companyMissions.length === 0 && companyFilter == null) setActiveTab(user ? 'active' : 'marketplace');
  }, [activeTab, companyMissions.length, user, companyFilter]);
  const [tagFilters, setTagFilters] = useState<FilterState>({
    domain: '', provider: '', certification: ''
  });
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);

  // LANGUE DE PRÉFÉRENCE (CCT-279). Elle filtre TOUTES les listes de l'accueil, pas seulement
  // le marketplace : une langue choisie qui ne s'appliquerait qu'à un onglet serait un réglage
  // à moitié vrai, et le joueur ne saurait pas lequel il regarde.
  const { langue: preferredLanguage, setLanguage: setPreferredLanguage, prete: languageReady } = usePreferredLanguage();
  // Démarrage EXPLICITE d'une partie (CCT-286) : choix de langue, gel, moteur, puis navigation.
  const { launch, dialog: launchDialog } = useMissionLaunch();
  // ⚠️ ON NE FILTRE PAS TANT QUE LA PRÉFÉRENCE N'EST PAS LUE. Le stockage est asynchrone :
  // filtrer avec le défaut pendant ce temps masquerait des missions que le joueur avait
  // choisi de voir, puis les ferait réapparaître — un clignotement qui ressemble à un bug.
  // ⚠️ LE FILTRE PORTE SUR LES LANGUES JOUABLES, PAS SUR LA LANGUE D'ORIGINE. Une mission
  // écrite en français et traduite en espagnol doit sortir sur le filtre « ES » : c'est
  // exactement ce que le joueur cherche en le posant.
  type WithLanguages = { langue?: string; langues?: string[] };
  const byLanguage = <T extends WithLanguages>(l: T[]): T[] =>
    (!languageReady || !preferredLanguage)
      ? l
      : l.filter((m) => missionMatchesLanguage(missionLanguages(m), preferredLanguage));
  // Langues RÉELLEMENT présentes dans le catalogue du joueur : proposer une langue dont
  // aucune mission n'existe donnerait un accueil vide sans explication.
  //
  // ⚠️ LE MARKETPLACE EN FAIT PARTIE. L'omettre laissait la barre invisible pour un visiteur
  // qui n'a encore aucune mission active — c'est-à-dire pour tout nouveau venu.
  const availableLanguages = useMemo(() => {
    const vues = new Set<string>();
    for (const m of [...activeMissions, ...trialMissions, ...companyMissions,
                     ...allMarketplaceMissions] as WithLanguages[]) {
      for (const v of missionLanguages(m)) vues.add(v);
    }
    return [...vues].sort();
  }, [activeMissions, trialMissions, companyMissions, allMarketplaceMissions]);

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return '#10B981';
      case 'Medium': return '#F59E0B';
      case 'Hard': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const renderStars = (rating: number) => {
    const stars: React.ReactNode[] = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    for (let i = 0; i < fullStars; i++) stars.push(<Ionicons key={`star-${i}`} name="star" size={16} color={colors.warning} />);
    if (hasHalfStar) stars.push(<Ionicons key="star-half" name="star-half" size={16} color={colors.warning} />);
    const remaining = 5 - fullStars - (hasHalfStar ? 1 : 0);
    for (let i = 0; i < remaining; i++) stars.push(<Ionicons key={`star-empty-${i}`} name="star-outline" size={16} color={colors.borderSecondary} />);
    return stars;
  };

  const normalize = (value?: string) => (value ?? '').trim();
  const normalizeUpper = (value?: string) => normalize(value).toUpperCase();
  const normalizeLower = (value?: string) => normalize(value).toLowerCase();

  const getMissionDomain = (mission: MarketplaceMission): FilterState['domain'] => {
    const taggedDomain = normalizeLower(mission.tags.domain);
    if (taggedDomain === 'cloud') return 'Cloud';
    if (taggedDomain === 'science') return 'Science';
    if (taggedDomain === 'languages') return 'Languages';

    const category = normalizeLower(mission.category);
    if (category === 'cloud computing') return 'Cloud';
    if (category === 'data science' || category === 'physics') return 'Science';
    if (category === 'language learning' || category === 'language arts') return 'Languages';

    const cert = normalizeUpper(mission.tags.certification);
    if (CLOUD_CERT_SET.has(cert)) return 'Cloud';
    if (SCIENCE_CERT_SET.has(cert)) return 'Science';
    if (LANGUAGE_CERT_SET.has(cert)) return 'Languages';

    if (
      cert.startsWith('AZ-') ||
      cert.startsWith('AWS') ||
      cert.startsWith('GCP') ||
      cert.startsWith('DP-') ||
      cert.startsWith('SC-') ||
      cert.startsWith('PL-') ||
      cert.startsWith('AI-')
    ) {
      return 'Cloud';
    }

    return 'Science';
  };

  const handleFilterChange = (level: keyof FilterState, value: string) => {
    setTagFilters(prev => {
      const next = { ...prev, [level]: value };
      // Changer de niveau réinitialise les niveaux plus profonds.
      if (level === 'domain') { next.provider = ''; next.certification = ''; }
      if (level === 'provider') { next.certification = ''; }
      return next;
    });
  };

  const clearFilters = () => setTagFilters({ domain: '', provider: '', certification: '' });

  const getFilteredMissions = () => {
    const selectedDomain = normalize(tagFilters.domain);
    const selectedCert = normalizeUpper(tagFilters.certification);

    // ⚠️ LE MARKETPLACE ÉCHAPPAIT AU FILTRE DE LANGUE. Il a sa propre chaîne de filtres (tags,
    // certification, missions déjà acquises) et ne passait pas par `byLanguage` : choisir « ES »
    // ne changeait rien à l'onglet où l'on voit le catalogue — c'est-à-dire le seul endroit où
    // le filtre a un sens pour un visiteur qui n'a pas encore de mission.
    return byLanguage(allMarketplaceMissions).filter(mission => {
      // Masque les missions déjà acquises (DB-driven via /me/stories).
      if (isOwned(mission.id)) return false;

      const missionDomain = getMissionDomain(mission);
      const cert = normalizeUpper(mission.tags.certification);
      const tech = normalizeUpper(mission.tags.technology);

      if (selectedDomain && missionDomain !== selectedDomain) return false;
      if (selectedCert) {
        // Certif précise sélectionnée.
        if (cert !== selectedCert && tech !== selectedCert) return false;
      } else if (tagFilters.provider) {
        // Provider sélectionné (sans certif) : la mission doit appartenir au groupe.
        const grp = (CERT_GROUPS[tagFilters.domain] || []).find(g => g.label === tagFilters.provider);
        const set = new Set((grp?.items || []).map(i => i.trim().toUpperCase()));
        const provUpper = normalizeUpper(tagFilters.provider);
        if (!set.has(cert) && !set.has(tech) && tech !== provUpper) return false;
      }
      return true;
    });
  };

  // ⚠️ LE COMPTEUR D'ONGLET ET LA LISTE DOIVENT VENIR DE LA MÊME SOURCE. Ils divergeaient :
  // l'onglet lisait `trialMissions.length` (NON filtré) pendant que la liste passait par
  // `byLanguage`. Résultat vu à l'écran : « Trial (1) » au-dessus d'une page blanche.
  const visibleActive = byLanguage(activeMissions);
  const visibleTrials = byLanguage(trialMissions);
  const hasActiveFilters = !!(tagFilters.domain || tagFilters.provider || tagFilters.certification);

  // INDICATION DES LANGUES PRISES EN CHARGE (CCT-279).
  //
  // ⚠️ ELLE MANQUAIT PARTOUT. Le joueur pouvait poser un filtre de langue sans qu'aucune carte
  // ne dise dans quelle langue elle se joue : le filtre semblait alors ne rien faire, ou pire,
  // faire disparaître des missions sans raison visible.
  //
  // Rien n'est affiché quand la mission n'en déclare aucune — une puce « ? » ferait passer une
  // donnée manquante pour une caractéristique du contenu.
  const renderLanguages = (mission: WithLanguages) => {
    const langues = missionLanguages(mission);
    if (langues.length === 0) return null;
    return (
      <View style={styles.languageRow}>
        <Ionicons name="language-outline" size={13} color={colors.textTertiary} />
        {langues.map((code) => (
          <View key={code} style={[styles.languageTag, { borderColor: colors.border,
            // La langue filtrée se repère d'un coup d'œil sur les cartes qui restent.
            backgroundColor: missionMatchesLanguage([code], preferredLanguage) && preferredLanguage
              ? colors.tabActiveBackground : 'transparent' }]}>
            <Text style={[styles.languageTagText, { color: colors.textSecondary }]}>
              {code.toUpperCase()}
            </Text>
          </View>
        ))}
      </View>
    );
  };

  const renderActiveMissionCard = (mission: ActiveMissionData) => (
    <View key={mission.id} style={[styles.missionCard, { backgroundColor: colors.cardBackground }]}>
      <View style={styles.missionHeader}>
        <View style={styles.missionTitleRow}>
          <View style={styles.missionTitleFlex}>
            <Text style={[styles.missionTitle, { color: colors.textPrimary }]}>{mission.title}</Text>
            {mission.subtitle && (
              <Text style={[styles.missionSubtitle, { color: colors.textSecondary }]}>{mission.subtitle}</Text>
            )}
          </View>
          <View style={styles.missionBadgesFlex}>
            {mission.certification && (
              <View style={styles.certPill}>
                <Text style={styles.certPillText}>{mission.certification}</Text>
              </View>
            )}
          </View>
        </View>
        <Text style={[styles.missionDescription, { color: colors.textSecondary }]} numberOfLines={2}>{mission.description}</Text>
      </View>
      <RemoteImage uris={[coverUri(mission), azuriteFallback(coverUri(mission))]}
        style={styles.missionCoverThumb} renderFallback={() => avatarBand(mission)} />
      <View style={styles.missionMeta}>
        <View style={styles.difficultyContainer}>
          <View style={[styles.difficultyDot, { backgroundColor: getDifficultyColor(mission.difficulty) }]} />
          <Text style={[styles.difficultyText, { color: colors.textSecondary }]}>{mission.difficulty}</Text>
        </View>
        <View style={styles.timeContainer}>
          <Ionicons name="time" size={12} color={colors.iconSecondary} />
          <Text style={[styles.timeText, { color: colors.textSecondary }]}> {mission.timeLeft === 'completed' ? t('common.completed') : mission.timeLeft === 'inProgress' ? t('common.inProgress') : mission.timeLeft}</Text>
        </View>
      </View>
      {renderLanguages(mission)}
      <View style={styles.progressContainer}>
        <View style={styles.progressHeader}>
          <Text style={[styles.progressLabel, { color: colors.textSecondary }]}>{t('card.progress')}</Text>
          <Text style={[styles.progressPercentage, { color: colors.textPrimary }]}>{mission.progress}%</Text>
        </View>
        <View style={[styles.progressBar, { backgroundColor: colors.border }]}>
          <View style={[styles.progressFill, { width: `${mission.progress}%` as any, backgroundColor: colors.primary }]} />
        </View>
      </View>
      {mission.locked ? (
        // Garde de licence (CCT-58) : mission verrouillée tant qu'aucune licence n'est attribuée.
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, padding: 12, borderRadius: 10, backgroundColor: colors.border }}>
          <Ionicons name="lock-closed" size={16} color={colors.textSecondary} />
          <Text style={{ flex: 1, color: colors.textSecondary, fontSize: 13, fontWeight: '600' }}>{t(mission.requiresInvitation ? 'mission.requireInvitation' : 'mission.requireLicense')}</Text>
        </View>
      ) : null}
      {/* CCT-119 — les 4 raccourcis (Chapters / Chat / Cards / Stories) ouvraient des écrans
          PARALLÈLES à l'histoire (listes plates, kanban de démo) : le joueur y perdait le fil de
          la mission. Tout passe désormais par la TIMELINE (« Continuer ») et la page de détails. */}
      <TouchableOpacity style={[styles.detailsButton, { borderColor: colors.borderSecondary }]} onPress={() => navigation.navigate('MissionDetail' as never, { missionId: mission.id } as never)}>
        <Ionicons name="information-circle-outline" size={16} color={colors.textSecondary} />
        <Text style={[styles.detailsButtonText, { color: colors.textSecondary }]}>{t('card.details')}</Text>
      </TouchableOpacity>
      {mission.progress === 100 ? (
        <TouchableOpacity style={[styles.claimButton, { backgroundColor: colors.primary }]}>
          <Ionicons name="trophy" size={16} color={colors.badgeText} />
          <Text style={[styles.claimButtonText, { color: colors.badgeText }]}> {t('mission.claimReward')}</Text>
        </TouchableOpacity>
      ) : (
        <TouchableOpacity
          style={[styles.continueButton, { backgroundColor: colors.primary }]}
          onPress={() => { navigation.navigate('MissionDetail' as never, { missionId: mission.id, autoLaunch: true } as never); }}
        >
          <Text style={[styles.continueButtonText, { color: colors.badgeText }]}>{t('mission.continue')}</Text>
        </TouchableOpacity>
      )}
    </View>
  );

  const renderTrialMissionCard = (mission: TrialMission) => (
    <View key={mission.id} style={[styles.missionCard, { backgroundColor: colors.cardBackground }]}>
      <View style={styles.missionHeader}>
        <View style={styles.missionTitleRow}>
          <View style={styles.missionTitleFlex}>
            <Text style={[styles.missionTitle, { color: colors.textPrimary }]}>{mission.title}</Text>
            {mission.subtitle && (
              <Text style={[styles.missionSubtitle, { color: colors.textSecondary }]}>{mission.subtitle}</Text>
            )}
          </View>
          <View style={styles.missionBadgesFlex}>
            <View style={[styles.trialBadge, { backgroundColor: colors.warning }]}>
              <Text style={[styles.trialText, { color: colors.badgeText }]}>{t('card.inTrial')}</Text>
            </View>
            {mission.remainingActions > 0 && (
              <View style={[styles.certPill, { backgroundColor: colors.warning + '33' }]}>
                <Text style={[styles.certPillText, { color: colors.warning }]}>{mission.remainingActions}</Text>
              </View>
            )}
          </View>
        </View>
        <Text style={[styles.missionDescription, { color: colors.textSecondary }]} numberOfLines={2}>{mission.description}</Text>
      </View>
      <RemoteImage uris={[coverUri(mission), azuriteFallback(coverUri(mission))]}
        style={styles.missionCoverThumb} renderFallback={() => avatarBand(mission)} />
      <View style={styles.missionMeta}>
        <View style={styles.difficultyContainer}>
          <View style={[styles.difficultyDot, { backgroundColor: getDifficultyColor(mission.difficulty) }]} />
          <Text style={[styles.difficultyText, { color: colors.textSecondary }]}>{mission.difficulty}</Text>
        </View>
        <View style={styles.timeContainer}>
          <Ionicons name="time" size={12} color={colors.iconSecondary} />
          <Text style={[styles.timeText, { color: colors.textSecondary }]}> {mission.timeLeft === 'completed' ? t('common.completed') : mission.timeLeft === 'inProgress' ? t('common.inProgress') : mission.timeLeft}</Text>
        </View>
      </View>
      {renderLanguages(mission)}
      <View style={styles.progressContainer}>
        <View style={styles.progressHeader}>
          <Text style={[styles.progressLabel, { color: colors.textSecondary }]}>{t('card.progress')}</Text>
          <Text style={[styles.progressPercentage, { color: colors.textPrimary }]}>{mission.progress}%</Text>
        </View>
        <View style={[styles.progressBar, { backgroundColor: colors.border }]}>
          <View style={[styles.progressFill, { width: `${mission.progress}%` as any, backgroundColor: colors.primary }]} />
        </View>
      </View>
      {mission.locked ? (
        // Garde de licence (CCT-58) : mission verrouillée tant qu'aucune licence n'est attribuée.
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, padding: 12, borderRadius: 10, backgroundColor: colors.border }}>
          <Ionicons name="lock-closed" size={16} color={colors.textSecondary} />
          <Text style={{ flex: 1, color: colors.textSecondary, fontSize: 13, fontWeight: '600' }}>{t(mission.requiresInvitation ? 'mission.requireInvitation' : 'mission.requireLicense')}</Text>
        </View>
      ) : null}
      {/* CCT-119 — les 4 raccourcis (Chapters / Chat / Cards / Stories) ouvraient des écrans
          PARALLÈLES à l'histoire (listes plates, kanban de démo) : le joueur y perdait le fil de
          la mission. Tout passe désormais par la TIMELINE (« Continuer ») et la page de détails. */}
      <TouchableOpacity style={[styles.detailsButton, { borderColor: colors.borderSecondary }]} onPress={() => navigation.navigate('MissionDetail' as never, { missionId: mission.id } as never)}>
        <Ionicons name="information-circle-outline" size={16} color={colors.textSecondary} />
        <Text style={[styles.detailsButtonText, { color: colors.textSecondary }]}>{t('card.details')}</Text>
      </TouchableOpacity>
      <View style={[styles.trialLimitations, { backgroundColor: isDarkTheme ? 'rgba(120,53,15,0.15)' : '#FEF3C7' }]}>
        <Text style={[styles.limitationsTitle, { color: isDarkTheme ? '#FCD34D' : '#92400E' }]}>{t('mission.trialLimitations')}</Text>
        {mission.trialLimitations.map((lim, i) => (
          <Text key={i} style={[styles.limitationText, { color: isDarkTheme ? '#FCD34D' : '#92400E' }]}>- {lim}</Text>
        ))}
      </View>
      <View style={styles.trialActions}>
        <TouchableOpacity
          style={[styles.inTrialButton, { backgroundColor: colors.primary + '22', borderColor: colors.primary, borderWidth: 1.5 }]}
          onPress={() => { navigation.navigate('MissionDetail' as never, { missionId: mission.id, autoLaunch: true } as never); }}
        >
          <Ionicons name="play-circle" size={16} color={colors.primary} />
          <Text style={[styles.inTrialButtonText, { color: colors.primary }]}>{t('mission.inTrialOpen')}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.buyButton, { backgroundColor: isInCart(mission.id) ? colors.success : colors.primary }]}
          onPress={() => { if (!isInCart(mission.id)) void addToCart(mission.id); }}
          disabled={isInCart(mission.id)}
        >
          <Ionicons name={isInCart(mission.id) ? 'checkmark-circle' : 'cart'} size={16} color={colors.badgeText} />
          <Text style={[styles.buyButtonText, { color: colors.badgeText }]}> {isInCart(mission.id) ? 'In Cart' : `$${mission.fullPrice}`}</Text>
        </TouchableOpacity>
      </View>
      <TouchableOpacity
        style={[styles.cancelTrialButton, { borderColor: colors.error }]}
        onPress={() => cancelTrial(mission.id)}
      >
        <Ionicons name="close-circle-outline" size={16} color={colors.error} />
        <Text style={[styles.cancelTrialButtonText, { color: colors.error }]}>{t('mission.cancelTrial')}</Text>
      </TouchableOpacity>
    </View>
  );

  const renderMarketplaceMissionCard = (mission: MarketplaceMission) => {
    const inTrial = isInTrial(mission.id);
    return (
      <View key={mission.id} style={[styles.courseCard, { backgroundColor: colors.cardBackground }]}>
        <View style={styles.coverWrapper}>
          <RemoteImage uris={[coverUri(mission), azuriteFallback(coverUri(mission))]}
            style={styles.coverImage}
            renderFallback={() => { const avs = getMissionAvatars(mission); return avs.length > 0 ? (
              <View style={[styles.coverImagePlaceholder, { backgroundColor: '#0F172A' }]}>
                <View style={styles.avatarBandRow}>
                  {avs.map((av, idx) => (
                    <View key={idx} style={[styles.avatarBandCircle, idx > 0 && styles.avatarBandOverlap]}>
                      <Image source={av} style={styles.avatarBandImg} resizeMode="cover" />
                    </View>
                  ))}
                </View>
              </View>
            ) : (
              <View style={[styles.coverImagePlaceholder, { backgroundColor: colors.surfaceSecondary }]}>
                <Ionicons name="image-outline" size={32} color={colors.textTertiary} />
              </View>
            ); }} />
          {mission.badgeImage && !failedBadges.has(mission.id) && (
            <View style={styles.badgeOverlay}>
              <Image
                source={mission.badgeImage}
                style={styles.badgeOverlayImage}
                resizeMode="contain"
                onError={() => markBadgeFailed(mission.id)}
              />
            </View>
          )}
        </View>
        <View style={styles.cardBody}>
          <View style={styles.cardHeader}>
            <View style={styles.titleRowContainer}>
              <View style={styles.titleFlex}>
                <Text style={[styles.courseTitle, { color: colors.textPrimary }]}>{mission.title}</Text>
                {mission.badgeImage && !failedBadges.has(mission.id) && (
                  <Image
                    source={mission.badgeImage}
                    style={styles.titleBadgeImage}
                    resizeMode="contain"
                    onError={() => markBadgeFailed(mission.id)}
                  />
                )}
              </View>
              <View style={styles.cardBadgesRow}>
                {mission.tags.certification && (
                  <View style={[styles.certBadge, { backgroundColor: colors.primary }]}>
                    <Text style={[styles.certBadgeText, { color: colors.badgeText }]}>{mission.tags.certification}</Text>
                  </View>
                )}
                {mission.isOnSale && (
                  <View style={[styles.saleBadge, { backgroundColor: colors.error }]}>
                    <Text style={[styles.saleText, { color: colors.badgeText }]}>{t('card.sale')}</Text>
                  </View>
                )}
              </View>
            </View>
            {mission.subtitle ? <Text style={[styles.courseSubtitle, { color: colors.textSecondary }]} numberOfLines={2}>{mission.subtitle}</Text> : null}
          </View>
          <View style={styles.metaContainer}>
            <View style={styles.difficultyContainer}>
              <View style={[styles.difficultyDot, { backgroundColor: getDifficultyColor(mission.difficulty) }]} />
              <Text style={[styles.difficultyText, { color: colors.textSecondary }]}>{mission.difficulty}</Text>
            </View>
            <Text style={[styles.durationText, { color: colors.textSecondary }]}>{mission.duration}</Text>
          </View>
          {renderLanguages(mission)}
          <View style={styles.ratingContainer}>
            <View style={styles.starsContainer}>{renderStars(mission.rating)}</View>
            <Text style={[styles.ratingText, { color: colors.textPrimary }]}>{mission.rating}</Text>
            <Text style={[styles.reviewsText, { color: colors.textSecondary }]}>{t('card.reviews', { count: mission.reviews })}</Text>
          </View>
          <View style={styles.priceContainer}>
            <View style={styles.priceInfo}>
              {mission.originalPrice && <Text style={[styles.originalPrice, { color: colors.textTertiary }]}>${mission.originalPrice}</Text>}
              <Text style={[styles.currentPrice, { color: colors.textPrimary }]}>${mission.price}</Text>
            </View>
            <View style={[styles.categoryBadge, { backgroundColor: colors.surfaceSecondary }]}>
              <Text style={[styles.categoryText, { color: colors.textSecondary }]}>{mission.category}</Text>
            </View>
          </View>
          <TouchableOpacity style={[styles.detailsButton, { borderColor: colors.borderSecondary, marginBottom: 8 }]} onPress={() => navigation.navigate('MissionDetail' as never, { missionId: mission.id } as never)}>
            <Ionicons name="information-circle-outline" size={16} color={colors.textSecondary} />
            <Text style={[styles.detailsButtonText, { color: colors.textSecondary }]}>{t('card.details')}</Text>
          </TouchableOpacity>
          <View style={styles.cardActionButtons}>
            <TouchableOpacity
              style={[styles.startTrialButton, { backgroundColor: inTrial ? colors.success + '22' : colors.surfaceSecondary, borderColor: inTrial ? colors.success : colors.borderSecondary }]}
              onPress={() => { if (!inTrial) void beginTrial(mission.id); }}
              disabled={inTrial}
            >
              <Ionicons name={inTrial ? 'checkmark-circle' : 'play'} size={16} color={inTrial ? colors.success : colors.textPrimary} />
              <Text style={[styles.startTrialText, { color: inTrial ? colors.success : colors.textPrimary }]}>{inTrial ? t('card.inTrial') : t('card.startTrial')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.buyNowButton, { backgroundColor: isInCart(mission.id) ? colors.success : colors.primary }]}
              onPress={() => { if (!isInCart(mission.id)) void addToCart(mission.id); }}
              disabled={isInCart(mission.id)}
            >
              <Ionicons name={isInCart(mission.id) ? 'checkmark-circle' : 'cart'} size={16} color={colors.badgeText} />
              <Text style={[styles.buyNowText, { color: colors.badgeText }]}>{isInCart(mission.id) ? t('card.inCart') : t('card.buy')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  };

  const renderCompanyMissionCard = (mission: CompanyMissionData) => {
    const enrolled = !!mission.status;
    return (
      <View key={mission.id} style={[styles.missionCard, { backgroundColor: colors.cardBackground }]}>
        <View style={styles.missionHeader}>
          <View style={styles.missionTitleRow}>
            <View style={styles.missionTitleFlex}>
              <Text style={[styles.missionTitle, { color: colors.textPrimary }]}>{mission.title}</Text>
              {mission.subtitle && (
                <Text style={[styles.missionSubtitle, { color: colors.textSecondary }]}>{mission.subtitle}</Text>
              )}
            </View>
            <View style={styles.missionBadgesFlex}>
              <View style={[styles.certPill, { backgroundColor: '#8B5CF622' }]}>
                <Text style={[styles.certPillText, { color: '#8B5CF6' }]}>{mission.companyName ?? 'Entreprise'}</Text>
              </View>
            </View>
          </View>
        </View>
        <View style={styles.missionMeta}>
          <View style={styles.difficultyContainer}>
            <View style={[styles.difficultyDot, { backgroundColor: getDifficultyColor(mission.difficulty) }]} />
            <Text style={[styles.difficultyText, { color: colors.textSecondary }]}>{mission.difficulty}</Text>
          </View>
          <Text style={[styles.timeText, { color: enrolled ? colors.success : colors.textSecondary }]}>
            {enrolled ? `Inscrit · ${mission.status}` : 'Disponible'}
          </Text>
        </View>
        <TouchableOpacity
          style={[styles.continueButton, { backgroundColor: colors.primary }]}
          onPress={() => { navigation.navigate('MissionDetail' as never, { missionId: mission.id, autoLaunch: true } as never); }}
        >
          <Text style={[styles.continueButtonText, { color: colors.badgeText }]}>{t(enrolled ? 'mission.continue' : 'mission.open')}</Text>
        </TouchableOpacity>
      </View>
    );
  };

  const filteredMissions = getFilteredMissions();
  // Catalogue visible (hors missions déjà acquises) : sert de total cohérent
  // pour l'onglet et la barre de résultats quand aucun filtre de tag n'est posé.
  // La langue en fait partie : sinon « 1 résultat / 14 » laisserait croire que treize missions
  // ont été écartées par les tags alors que c'est le filtre de langue qui les a retirées.
  const marketplaceVisibleCount = byLanguage(allMarketplaceMissions).filter(m => !isOwned(m.id)).length;

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        {/* Titre + sous-titre EMPILÉS (colonne) : en largeur étroite (mobile), les
            garder côte à côte faisait se replier le sous-titre par-dessus le titre. */}
        <View style={styles.headerLeft}>
          <View style={styles.headerContent}>
            <FontAwesome name="bullseye" size={24} color={colors.iconPrimary} />
            <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>{t('home.title')}</Text>
          </View>
          <Text style={[styles.subtitleText, { color: colors.textSecondary }]} numberOfLines={2}>
            {activeTab === 'active' ? t('missions.sub.active') : activeTab === 'trial' ? t('missions.sub.trial') : activeTab === 'company' ? t('missions.companyByRole') : t('home.subtitle')}
          </Text>
        </View>
        <View style={styles.headerActions}>
          {!user && (
            <TouchableOpacity style={{ backgroundColor: '#4F46E5', paddingHorizontal: 16, paddingVertical: 8, borderRadius: 8 }} onPress={() => navigation.navigate('Login' as never)}>
              <Text style={{ color: '#FFF', fontWeight: '600', fontSize: 14 }}>{t('home.signIn')}</Text>
            </TouchableOpacity>
          )}
          {/* La barre globale DesktopLayout (avec panier) n'existe QUE pour un
              utilisateur connecté en desktop. On affiche donc ce panier sinon :
              mobile, OU invité desktop (PublicNavigator, sans barre globale). */}
          {(!isDesktop || !user) && (
            <TouchableOpacity style={styles.cartButton} onPress={() => navigation.navigate('Cart' as never)}>
              <Ionicons name="cart" size={24} color={colors.iconPrimary} />
              {cartItems.length > 0 && (
                <View style={[styles.cartBadge, { backgroundColor: colors.error }]}>
                  <Text style={[styles.cartBadgeText, { color: '#FFF' }]}>{cartItems.length}</Text>
                </View>
              )}
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* SÉLECTEUR DE LANGUE — CCT-279. Placé AU-DESSUS des onglets : il porte sur tout
          l'accueil, pas sur l'onglet courant. Sous les onglets, il aurait l'air d'un filtre
          de la liste affichée, et changer d'onglet donnerait l'impression de le perdre.
          Il n'apparaît QUE si le catalogue propose plusieurs langues : un sélecteur à un
          seul choix est un réglage qui ne règle rien. */}
      {availableLanguages.length > 1 && (
        <View style={[styles.languageBar, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
          <Ionicons name="language-outline" size={15} color={colors.iconSecondary} />
          <Text style={[styles.languageTitle, { color: colors.textSecondary }]}>{t('home.language.title')}</Text>
          {/* « Toutes » n'est pas une langue : c'est l'absence de filtre. Sans cette
              possibilité, un joueur ayant choisi une langue ne pourrait plus voir le reste
              du catalogue sans deviner quelle langue sélectionner. */}
          <TouchableOpacity
            onPress={() => setPreferredLanguage('')}
            style={[styles.languageChip, { borderColor: colors.border,
              backgroundColor: !preferredLanguage ? colors.tabActiveBackground : 'transparent' }]}
            activeOpacity={0.7}>
            <Text style={[styles.languageChipText,
              { color: !preferredLanguage ? colors.tabActive : colors.textSecondary }]}>{t('home.language.all')}</Text>
          </TouchableOpacity>
          {availableLanguages.map((l) => (
            <TouchableOpacity key={l}
              onPress={() => setPreferredLanguage(l)}
              style={[styles.languageChip, { borderColor: colors.border,
                backgroundColor: preferredLanguage === l ? colors.tabActiveBackground : 'transparent' }]}
              activeOpacity={0.7}>
              <Text style={[styles.languageChipText,
                { color: preferredLanguage === l ? colors.tabActive : colors.textSecondary }]}>
                {l.toUpperCase()}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      )}

      <View style={[styles.tabsContainer, { backgroundColor: colors.surface }]}>
        {user && (
          <TouchableOpacity style={[styles.tab, { backgroundColor: activeTab === 'active' ? colors.tabActiveBackground : 'transparent' }, activeTab === 'active' && styles.activeTab]} onPress={() => setActiveTab('active')}>
            <Text style={[styles.tabText, { color: activeTab === 'active' ? colors.tabActive : colors.tabInactive }]}>{t('home.tab.active', { count: visibleActive.length })}</Text>
          </TouchableOpacity>
        )}
        {user && (
          <TouchableOpacity style={[styles.tab, { backgroundColor: activeTab === 'trial' ? colors.tabActiveBackground : 'transparent' }, activeTab === 'trial' && styles.activeTab]} onPress={() => setActiveTab('trial')}>
            <Text style={[styles.tabText, { color: activeTab === 'trial' ? colors.tabActive : colors.tabInactive }]}>{t('home.tab.trial', { count: visibleTrials.length })}</Text>
          </TouchableOpacity>
        )}
        {user && (companyMissions.length > 0 || companyFilter != null) && (
          <TouchableOpacity style={[styles.tab, { backgroundColor: activeTab === 'company' ? colors.tabActiveBackground : 'transparent' }, activeTab === 'company' && styles.activeTab]} onPress={() => setActiveTab('company')}>
            <Text style={[styles.tabText, { color: activeTab === 'company' ? colors.tabActive : colors.tabInactive }]}>
              {companyFilter != null ? `${companyFilterName ?? 'Entreprise'} (${shownCompanyMissions.length})` : `Company (${companyMissions.length})`}
            </Text>
          </TouchableOpacity>
        )}
        <TouchableOpacity style={[styles.tab, { backgroundColor: activeTab === 'marketplace' ? colors.tabActiveBackground : 'transparent' }, activeTab === 'marketplace' && styles.activeTab]} onPress={() => setActiveTab('marketplace')}>
          <Text style={[styles.tabText, { color: activeTab === 'marketplace' ? colors.tabActive : colors.tabInactive }]}>{t('home.tab.marketplace', { count: marketplaceVisibleCount })}</Text>
        </TouchableOpacity>
      </View>

      {activeTab === 'marketplace' && (
        <View
          style={[
            styles.filtersContainer,
            { backgroundColor: colors.surface, borderBottomColor: colors.border },
            isWebDesktop && styles.filtersContainerDesktop,
          ]}
        >
          <View style={styles.filterHeader}>
            <Ionicons name="filter" size={16} color={colors.iconSecondary} />
            <Text style={[styles.filterTitle, { color: colors.textPrimary }]}>{t('home.filters.title')}</Text>
            {hasActiveFilters && (
              <TouchableOpacity style={styles.clearButton} onPress={clearFilters}>
                <Ionicons name="close" size={14} color={colors.iconSecondary} />
                <Text style={[styles.clearButtonText, { color: colors.textSecondary }]}>{t('home.facet.clear')}</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity onPress={() => setFiltersCollapsed(c => !c)} style={styles.filterChevron} activeOpacity={0.7}>
              <Ionicons name={filtersCollapsed ? 'chevron-down' : 'chevron-up'} size={18} color={colors.iconSecondary} />
            </TouchableOpacity>
          </View>
          {!filtersCollapsed && (
            <>
              <Text style={[styles.filterResultsHint, { color: colors.textSecondary }]}>
                {t('home.results', { count: filteredMissions.length })}{filteredMissions.length > 0 ? t('home.results.scroll') : ''}
              </Text>
              <ScrollView
                style={Platform.OS !== 'web' ? styles.filterScroll : undefined}
                nestedScrollEnabled
                showsVerticalScrollIndicator={false}
              >
                <View style={styles.filterSection}>
                  <Text style={[styles.filterLabel, { color: colors.textSecondary }]}>{t('home.facet.domain')}</Text>
                  <View style={styles.filterOptions}>
                    {['Cloud', 'Science', 'Languages'].map((domain) => (
                      <TouchableOpacity
                        key={domain}
                        style={[styles.filterOption, { backgroundColor: tagFilters.domain === domain ? colors.primary : colors.surfaceSecondary }]}
                        onPress={() => handleFilterChange('domain', tagFilters.domain === domain ? '' : domain)}
                      >
                        <Text style={[styles.filterOptionText, { color: tagFilters.domain === domain ? colors.badgeText : colors.textSecondary }]}>{domain}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
                {tagFilters.domain !== '' && (() => {
                  const groups = CERT_GROUPS[tagFilters.domain] || [];
                  // Domaine multi-provider (Cloud) : on choisit le provider AVANT la certif.
                  if (groups.length > 1) {
                    const selectedGroup = groups.find(g => g.label === tagFilters.provider);
                    return (
                      <>
                        <View style={styles.filterSection}>
                          <Text style={[styles.filterLabel, { color: colors.textSecondary }]}>{t('home.facet.cloudProvider')}</Text>
                          <View style={styles.filterOptions}>
                            {groups.map((group) => (
                              <TouchableOpacity
                                key={group.label}
                                style={[styles.filterOption, { backgroundColor: tagFilters.provider === group.label ? colors.primary : colors.surfaceSecondary }]}
                                onPress={() => handleFilterChange('provider', tagFilters.provider === group.label ? '' : group.label)}
                              >
                                <Text style={[styles.filterOptionText, { color: tagFilters.provider === group.label ? colors.badgeText : colors.textSecondary }]}>{group.label}</Text>
                              </TouchableOpacity>
                            ))}
                          </View>
                        </View>
                        {selectedGroup && (
                          <View style={styles.filterSection}>
                            <Text style={[styles.filterLabel, { color: colors.textSecondary }]}>{t('home.facet.certifications')}</Text>
                            <View style={styles.filterOptions}>
                              {selectedGroup.items.map((cert) => (
                                <TouchableOpacity
                                  key={cert}
                                  style={[styles.filterOption, { backgroundColor: tagFilters.certification === cert ? colors.primary : colors.surfaceSecondary }]}
                                  onPress={() => handleFilterChange('certification', tagFilters.certification === cert ? '' : cert)}
                                >
                                  <Text style={[styles.filterOptionText, { color: tagFilters.certification === cert ? colors.badgeText : colors.textSecondary }]}>{cert}</Text>
                                </TouchableOpacity>
                              ))}
                            </View>
                          </View>
                        )}
                      </>
                    );
                  }
                  // Domaine simple (Science / Languages) : items directs.
                  return (
                    <View style={styles.filterSection}>
                      <Text style={[styles.filterLabel, { color: colors.textSecondary }]}>{t('home.facet.topics')}</Text>
                      <View style={styles.filterOptions}>
                        {groups.flatMap(g => g.items).map((cert) => (
                          <TouchableOpacity
                            key={cert}
                            style={[styles.filterOption, { backgroundColor: tagFilters.certification === cert ? colors.primary : colors.surfaceSecondary }]}
                            onPress={() => handleFilterChange('certification', tagFilters.certification === cert ? '' : cert)}
                          >
                            <Text style={[styles.filterOptionText, { color: tagFilters.certification === cert ? colors.badgeText : colors.textSecondary }]}>{cert}</Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    </View>
                  );
                })()}
              </ScrollView>
            </>
          )}
        </View>
      )}

      <ScrollView style={[styles.content, { backgroundColor: colors.background }]} contentContainerStyle={{ width: '100%', flexGrow: 1 }} showsVerticalScrollIndicator={false}>
        {activeTab === 'marketplace' && (
          <View style={[styles.resultsBar, { backgroundColor: colors.surfaceSecondary, borderColor: colors.borderSecondary }]}>
            {marketplaceLoading && allMarketplaceMissions.length === 0 ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                <ActivityIndicator size="small" color={colors.primary} style={{ marginRight: 8 }} />
                <Text style={[styles.resultsBarText, { color: colors.textSecondary }]}>
                  {t('home.loading')}
                </Text>
              </View>
            ) : (
              <Text style={[styles.resultsBarText, { color: colors.textSecondary }]}>
                {t('home.results.ratio', { shown: filteredMissions.length, total: marketplaceVisibleCount })}
              </Text>
            )}
          </View>
        )}
        <View style={isWebDesktop ? [styles.missionsList, styles.desktopThreeColGrid] : styles.missionsList}>
          {activeTab === 'active' && visibleActive.map(renderActiveMissionCard)}
          {activeTab === 'trial' && visibleTrials.map(renderTrialMissionCard)}
          {/* ⚠️ CES DEUX ONGLETS N'AVAIENT AUCUN ÉTAT VIDE. Quand le filtre de langue écartait
              tout, la page était simplement blanche — et rien ne disait qu'un filtre était
              posé, ni comment le lever. Un écran vide sans explication se lit comme une panne. */}
          {activeTab === 'active' && visibleActive.length === 0 && (
            <EmptyState colors={colors} hidden={activeMissions.length}
              langue={preferredLanguage} onLever={() => setPreferredLanguage('')}
              rien={t('home.empty.noActive')} />
          )}
          {activeTab === 'trial' && visibleTrials.length === 0 && (
            <EmptyState colors={colors} hidden={trialMissions.length}
              langue={preferredLanguage} onLever={() => setPreferredLanguage('')}
              rien={t('home.empty.noTrial')} />
          )}
          {activeTab === 'company' && shownCompanyMissions.map(renderCompanyMissionCard)}
          {activeTab === 'company' && shownCompanyMissions.length === 0 && (
            <View style={[styles.emptyState, { backgroundColor: colors.surfaceSecondary, borderColor: colors.borderSecondary }]}>
              <Text style={[styles.emptyStateText, { color: colors.textSecondary }]}>
                {companyFilter != null
                  ? t('missions.company.noneFor', { name: companyFilterName ?? t('missions.company.thisCompany') })
                  : t('missions.company.none')}
              </Text>
            </View>
          )}
          {activeTab === 'marketplace' && filteredMissions.map(renderMarketplaceMissionCard)}
          {/* Layout shell : squelette de cartes en 3 colonnes pendant le chargement (CCT-95). */}
          {activeTab === 'marketplace' && filteredMissions.length === 0 && marketplaceLoading &&
            [0, 1, 2, 3, 4, 5].map((i) => <SkeletonCard key={`sk-${i}`} colors={colors} />)}
          {activeTab === 'marketplace' && filteredMissions.length === 0 && !marketplaceLoading && (
            <View style={[styles.emptyState, { backgroundColor: colors.surfaceSecondary, borderColor: colors.borderSecondary }]}>
              <Ionicons name="search" size={16} color={colors.iconSecondary} />
              <Text style={[styles.emptyStateText, { color: colors.textSecondary }]}>
                {t('home.empty.noResult')}
              </Text>
              <TouchableOpacity onPress={clearFilters}>
                <Text style={[styles.emptyStateAction, { color: colors.primary }]}>{t('home.empty.reset')}</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </ScrollView>
      {launchDialog}
    </SafeAreaView>
  );
}

// Carte squelette (layout shell) affichée pendant le chargement du marketplace (CCT-95).
// Reprend la forme d'une carte de mission : visuel + titre + lignes + badges.
/**
 * État vide des onglets « actives » et « essai ».
 *
 * ⚠️ IL DISTINGUE « IL N'Y EN A PAS » DE « LE FILTRE LES CACHE ». Confondre les deux est ce qui
 * rendait la page blanche indéchiffrable : l'onglet annonçait une mission, l'écran n'en montrait
 * aucune, et rien ne disait qu'un filtre de langue était posé — encore moins comment le lever.
 */
function EmptyState({ colors, hidden, langue, onLever, rien }: {
  colors: any; hidden: number; langue: string; onLever: () => void; rien: string;
}) {
  const t = useT();
  const filter = !!langue && hidden > 0;
  return (
    <View style={[styles.emptyState, { backgroundColor: colors.surfaceSecondary, borderColor: colors.borderSecondary }]}>
      <Ionicons name={filter ? 'language-outline' : 'albums-outline'} size={16} color={colors.iconSecondary} />
      <Text style={[styles.emptyStateText, { color: colors.textSecondary }]}>
        {filter
          ? t('missions.hiddenByFilter', { count: hidden, language: langue.toUpperCase() })
          : rien}
      </Text>
      {filter && (
        <TouchableOpacity onPress={onLever}>
          <Text style={[styles.emptyStateAction, { color: colors.primary }]}>{t('missions.seeAllLanguages')}</Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

function SkeletonCard({ colors }: { colors: any }) {
  const block = (w: any, h: number, mt = 0, r = 6) => (
    <View style={{ width: w, height: h, marginTop: mt, borderRadius: r, backgroundColor: colors.surfaceSecondary, opacity: 0.6 }} />
  );
  return (
    <View style={[styles.missionCard, { backgroundColor: colors.cardBackground }]}>
      {block('100%', 120, 0, 8)}
      {block('70%', 16, 14)}
      {block('92%', 10, 12)}
      {block('80%', 10, 6)}
      <View style={{ flexDirection: 'row', gap: 8, marginTop: 14 }}>
        {block(56, 22, 0, 999)}{block(56, 22, 0, 999)}{block(56, 22, 0, 999)}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: { paddingHorizontal: 20, paddingVertical: 16, borderBottomWidth: 1, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  headerLeft: { flex: 1, marginRight: 12 },
  headerActions: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  headerContent: { flexDirection: 'row', alignItems: 'center' },
  headerTitle: { fontSize: 24, fontWeight: 'bold', marginLeft: 8 },
  headerSubtitle: { flex: 1, marginLeft: 16 },
  subtitleText: { fontSize: 14, marginLeft: 32, marginTop: 2 },
  cartButton: { position: 'relative', padding: 8 },
  cartBadge: { position: 'absolute', top: 0, right: 0, borderRadius: 10, width: 20, height: 20, justifyContent: 'center', alignItems: 'center' },
  cartBadgeText: { fontSize: 12, fontWeight: 'bold' },
  languageBar: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 16,
    paddingVertical: 8, borderBottomWidth: 1, flexWrap: 'wrap' },
  languageTitle: { fontSize: 12, fontWeight: '600', marginRight: 4 },
  // Rangée d'indication sur la CARTE : discrète (elle ne doit pas concurrencer le titre ni le
  // prix), mais présente sur chaque mission qui déclare une langue.
  languageRow: { flexDirection: 'row', alignItems: 'center', gap: 5, flexWrap: 'wrap',
    marginTop: 2, marginBottom: 6 },
  languageTag: { paddingHorizontal: 7, paddingVertical: 2, borderRadius: 999, borderWidth: 1 },
  languageTagText: { fontSize: 10, fontWeight: '700', letterSpacing: 0.4 },
  languageChip: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 999, borderWidth: 1 },
  languageChipText: { fontSize: 11, fontWeight: '700' },
  tabsContainer: { flexDirection: 'row', marginHorizontal: 16, marginTop: 16, borderRadius: 8, padding: 4 },
  tab: { flex: 1, paddingVertical: 8, paddingHorizontal: 12, borderRadius: 6, alignItems: 'center' },
  activeTab: { shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.1, shadowRadius: 2, elevation: 2 },
  tabText: { fontSize: 14, fontWeight: '500' },
  filtersContainer: { padding: 16, borderBottomWidth: 1 },
  filtersContainerDesktop: { maxHeight: 280, overflow: 'auto' } as any,
  filterScroll: { maxHeight: 280 }, // natif : rend les filtres défilables (Android)
  filterHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  filterTitle: { fontSize: 14, fontWeight: '600', marginLeft: 8, flex: 1 },
  filterChevron: { marginLeft: 8, padding: 4 },
  filterResultsHint: { fontSize: 12, marginBottom: 10 },
  clearButton: { flexDirection: 'row', alignItems: 'center', marginLeft: 'auto' as any },
  clearButtonText: { fontSize: 12, marginLeft: 4 },
  filterSection: { marginBottom: 12 },
  filterLabel: { fontSize: 12, fontWeight: '600', marginBottom: 8 },
  filterOptions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  filterOption: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16 },
  filterOptionText: { fontSize: 12 },
  certGroup: { marginBottom: 10 },
  certGroupLabel: { fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  content: { flex: 1 },
  missionsList: { padding: 16 },
  missionCard: { borderRadius: 12, padding: 16, marginBottom: 12, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.1, shadowRadius: 3, elevation: 2 },
  missionHeader: { marginBottom: 12 },
  missionTitleRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 4 },
  missionTitleFlex: { flex: 2 },
  missionBadgesFlex: { flex: 1, alignItems: 'flex-end', flexWrap: 'wrap' },
  missionTitle: { fontSize: 18, fontWeight: 'bold' },
  missionSubtitle: { fontSize: 13, fontStyle: 'italic', marginTop: 2, lineHeight: 18 },
  missionDescription: { fontSize: 14, lineHeight: 20 },
  missionBadges: { alignItems: 'flex-end' },
  missionInfo: { flex: 1, marginRight: 12 },
  titleContainer: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 },
  trialBadge: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6, marginBottom: 4 },
  trialText: { fontSize: 12, fontWeight: '600' },
  rewardBadge: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6, marginBottom: 4 },
  rewardText: { fontSize: 12, fontWeight: '600' },
  certPill: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12, backgroundColor: 'rgba(99,102,241,0.18)', marginTop: 4 },
  certPillText: { fontSize: 11, fontWeight: '700', color: '#6366F1', letterSpacing: 0.5 },
  // Ratio 16/9 (format de la widget) et PAS de hauteur fixe : une boîte 100 px de haut sur
  // toute la largeur de la carte fait ~4.7:1, dans laquelle « cover » tronquait violemment le
  // visuel — quel que soit le soin apporté au cadrage en Gallery.
  missionCoverThumb: { width: '100%', aspectRatio: 16 / 9, borderRadius: 8, marginBottom: 12 } as any,
  missionMeta: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  difficultyContainer: { flexDirection: 'row', alignItems: 'center' },
  difficultyDot: { width: 8, height: 8, borderRadius: 4, marginRight: 6 },
  difficultyText: { fontSize: 12 },
  timeContainer: { flexDirection: 'row', alignItems: 'center' },
  timeText: { fontSize: 12 },
  progressContainer: { marginBottom: 12 },
  progressHeader: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  progressLabel: { fontSize: 14 },
  progressPercentage: { fontSize: 14, fontWeight: '600' },
  progressBar: { height: 6, borderRadius: 3, overflow: 'hidden' },
  progressFill: { height: '100%' as any, borderRadius: 3 },
  trialLimitations: { marginBottom: 12, padding: 12, borderRadius: 6 },
  limitationsTitle: { fontSize: 12, fontWeight: '600', marginBottom: 4 },
  limitationText: { fontSize: 11, lineHeight: 16 },
  claimButton: { paddingVertical: 10, borderRadius: 8, alignItems: 'center', flexDirection: 'row', justifyContent: 'center', marginTop: 8, gap: 6 },
  claimButtonText: { fontSize: 14, fontWeight: '600' },
  continueButton: { paddingVertical: 10, borderRadius: 8, alignItems: 'center', marginTop: 8 },
  continueButtonText: { fontSize: 14, fontWeight: '600' },
  trialActions: { flexDirection: 'row', gap: 8, marginTop: 8 },
  inTrialButton: { flex: 1, paddingVertical: 10, borderRadius: 8, alignItems: 'center', flexDirection: 'row', justifyContent: 'center', gap: 6 },
  inTrialButtonText: { fontSize: 14, fontWeight: '600' },
  buyButton: { flex: 1, paddingVertical: 10, borderRadius: 8, alignItems: 'center', flexDirection: 'row', justifyContent: 'center', gap: 6 },
  buyButtonText: { fontSize: 14, fontWeight: '600' },
  cancelTrialButton: { marginTop: 8, paddingVertical: 10, borderRadius: 8, borderWidth: 1.5, alignItems: 'center', flexDirection: 'row', justifyContent: 'center', gap: 6 },
  cancelTrialButtonText: { fontSize: 14, fontWeight: '600' },
  courseCard: { borderRadius: 12, marginBottom: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.1, shadowRadius: 3, elevation: 2, overflow: 'hidden' },
  coverWrapper: { position: 'relative' },
  coverImage: { width: '100%' as any, height: 160 },
  coverImagePlaceholder: { width: '100%' as any, height: 160, alignItems: 'center', justifyContent: 'center' },
  badgeOverlay: { position: 'absolute', top: 8, right: 8, width: 56, height: 56, borderRadius: 10, backgroundColor: 'rgba(255,255,255,0.95)', alignItems: 'center', justifyContent: 'center', padding: 4 },
  badgeOverlayImage: { width: 48, height: 48 },
  cardBody: { padding: 16 },
  cardHeader: { marginBottom: 12 },
  titleRowContainer: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 4 },
  titleFlex: { flex: 2, flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  cardBadgesRow: { flex: 1, flexDirection: 'row', gap: 6, flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'flex-end', marginLeft: 8 },
  certBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 },
  certBadgeText: { fontSize: 11, fontWeight: '700', letterSpacing: 0.3 },
  courseTitle: { fontSize: 18, fontWeight: 'bold' },
  courseSubtitle: { fontSize: 13, lineHeight: 18, fontStyle: 'italic', marginTop: 2 },
  saleBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 },
  saleText: { fontSize: 11, fontWeight: '600' },
  metaContainer: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  durationText: { fontSize: 12 },
  ratingContainer: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  starsContainer: { flexDirection: 'row', marginRight: 8 },
  ratingText: { fontSize: 14, fontWeight: '600', marginRight: 4 },
  reviewsText: { fontSize: 12 },
  priceContainer: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  priceInfo: { flexDirection: 'row', alignItems: 'center' },
  originalPrice: { fontSize: 14, textDecorationLine: 'line-through', marginRight: 8 },
  currentPrice: { fontSize: 18, fontWeight: 'bold' },
  categoryBadge: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6 },
  categoryText: { fontSize: 12, fontWeight: '500' },
  cardActionButtons: { flexDirection: 'row', gap: 8 },
  startTrialButton: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', borderWidth: 1, paddingVertical: 10, borderRadius: 8, gap: 6 },
  startTrialText: { fontSize: 14, fontWeight: '600' },
  buyNowButton: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 10, borderRadius: 8, gap: 6 },
  buyNowText: { fontSize: 14, fontWeight: '600' },
  detailsButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 8, borderRadius: 8, borderWidth: 1, marginBottom: 8, gap: 6 },
  detailsButtonText: { fontSize: 13, fontWeight: '500' },
  titleWithBadge: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flex: 1, gap: 8 },
  titleBadgeImage: { width: 48, height: 48, flexShrink: 0 },
  avatarBand: { alignItems: 'center', justifyContent: 'center' },
  avatarBandRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center' },
  avatarBandCircle: { width: 50, height: 50, borderRadius: 25, overflow: 'hidden', borderWidth: 2, borderColor: 'rgba(255,255,255,0.3)' },
  avatarBandOverlap: { marginLeft: -15 },
  avatarBandImg: { width: 50, height: 50, borderRadius: 25 },
  desktopThreeColGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', alignItems: 'stretch', gap: 16 } as any,
  resultsBar: { marginHorizontal: 16, marginTop: 12, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8, borderWidth: 1 },
  resultsBarText: { fontSize: 12, fontWeight: '600' },
  emptyState: { width: '100%', borderWidth: 1, borderRadius: 10, padding: 16, alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 120 },
  emptyStateText: { fontSize: 13 },
  emptyStateAction: { fontSize: 13, fontWeight: '600' },
});




